/**
 * Navbar Component
 * Original layout: all nav links visible on desktop, mobile slide-in menu
 * Home & About: transparent when at top. Other pages: solid background.
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { NAV_LINKS } from '../../constants/navigation';
import { BRAND } from '../../constants/images';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigatedAtRef = React.useRef(0);

  const isActive = (path) => location.pathname === path;
  const isHeroPage = location.pathname === '/' || location.pathname === '/about';

  // On hero pages: transparent when not scrolled. Other pages: always solid.
  const hasSolidBg = !isHeroPage || isScrolled;

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsScrolled(false);
    navigatedAtRef.current = Date.now();
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      const gracePeriodMs = 400;
      if (Date.now() - navigatedAtRef.current < gracePeriodMs) return;
      setIsScrolled(scrollY > 50);
    };

    const timeout = setTimeout(() => {
      setIsScrolled((window.scrollY || window.pageYOffset) > 50);
    }, 450);

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeout);
    };
  }, [location.pathname]);

  const iconClass = hasSolidBg ? 'text-[#1A1A1A]' : 'text-white';

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          hasSolidBg ? 'bg-[#FAF8F2]/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to="/" className="flex items-center gap-1 group" data-testid="navbar-logo">
              <motion.div
                className="flex items-center gap-1"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ scale: 1.02 }}
              >
                <img
                  src={BRAND.logo}
                  alt=""
                  className="logo-img h-9 sm:h-10 w-auto object-contain"
                  data-logo
                  aria-hidden
                />
                <span
                  className={`text-lg sm:text-xl font-semibold tracking-tight transition-colors duration-200 ${
                    hasSolidBg
                      ? 'text-[#1A1A1A] group-hover:text-[#8B7355]'
                      : 'text-white/95 group-hover:text-white'
                  }`}
                >
                  <span className="font-bold tracking-wide">BISI</span>
                  <span className="font-light tracking-[0.2em] ml-1 sm:ml-1.5 text-[0.85em] sm:text-[0.9em]">Engineering</span>
                </span>
              </motion.div>
            </Link>

            {/* Desktop: all nav links visible - wave effect via staggerChildren */}
            <motion.div
              className="hidden lg:flex items-center gap-1"
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: {
                  transition: {
                    staggerChildren: 0.05,
                    delayChildren: 0.1,
                  },
                },
              }}
            >
              {NAV_LINKS.map((link) => (
                <motion.div
                  key={link.path}
                  variants={{
                    hidden: { opacity: 0, y: -10 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } },
                  }}
                >
                  <Link
                    to={link.path}
                    className={`relative px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                      isActive(link.path)
                        ? 'text-[#8B7355]'
                        : hasSolidBg
                          ? 'text-[#1A1A1A] hover:text-[#8B7355]'
                          : 'text-white/90 hover:text-white'
                    }`}
                    data-testid={`nav-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                    {isActive(link.path) && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute bottom-0 left-4 right-4 h-0.5 bg-[#8B7355]"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                  </Link>
                </motion.div>
              ))}
              <motion.div variants={{ hidden: { opacity: 0, y: -10 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } } }}>
              <Link
                to="/contact"
                className={`ml-2 px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold tracking-wide transition-all duration-300 ${
                  hasSolidBg
                    ? 'text-white bg-[#1A1A1A] hover:bg-[#8B7355]'
                    : 'text-[#1A1A1A] bg-white hover:bg-[#8B7355] hover:text-white'
                }`}
                data-testid="navbar-cta"
              >
                Get in Touch
              </Link>
              </motion.div>
            </motion.div>

            {/* Mobile: hamburger menu */}
            <motion.button
              className="lg:hidden p-2 -mr-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
              whileTap={{ scale: 0.95 }}
            >
              <AnimatePresence mode="wait">
                {isMobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                  >
                    <X className={`w-6 h-6 ${iconClass}`} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                  >
                    <Menu className={`w-6 h-6 ${iconClass}`} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay - slide in from right */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            </motion.div>

            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed top-0 right-0 h-full w-72 sm:w-80 max-w-full bg-[#FAF8F2] shadow-2xl z-50 lg:hidden"
              data-testid="mobile-menu"
            >
              <div className="p-4 sm:p-6 pt-20 sm:pt-24">
                <div className="space-y-1">
                  {NAV_LINKS.map((link, index) => (
                    <motion.div
                      key={link.path}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 + index * 0.05 }}
                    >
                      <Link
                        to={link.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block px-4 py-3 text-base sm:text-lg font-medium transition-colors ${
                          isActive(link.path)
                            ? 'text-[#8B7355] bg-[#8B7355]/5'
                            : 'text-[#1A1A1A] hover:text-[#8B7355] hover:bg-[#F5F3F0]'
                        }`}
                      >
                        {link.name}
                      </Link>
                    </motion.div>
                  ))}
                </div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-[#E0DDD9]"
                >
                  <Link
                    to="/contact"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full text-center px-6 py-3 sm:py-4 text-sm font-semibold text-white bg-[#8B7355] hover:bg-[#7A6348] transition-colors"
                  >
                    Get in Touch
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
